import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class Club extends BaseObj {
    public ID: number;
    public __id: number;

    public FederationIDS: number[];
    public __federationids: number[];

    public isTeam: boolean;
    public __isteam: boolean;

    public Name: string;
    public __name: string;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        EntityState?: EntityState;
        FederationIDS?: number[];
        isTeam?: boolean;
        Name?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<Club>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('Club tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.FederationIDS = values.FederationIDS ?? [];
        this.__federationids = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.FederationIDS ?? [],
            callback: (oldVal, newVal) => this.__entityStateHandler.propertyChanged('FederationIDS', oldVal, newVal),
        });
        Object.defineProperty(this, 'FederationIDS', {
            get(): number[] {
                return this.__federationids;
            },
            set(value: number[]) {
                this.__entityStateHandler.propertyChanged('FederationIDS', this.__federationids, value);
                this.__federationids = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (oldVal: number[], newVal: number[]) =>
                        this.__entityStateHandler.propertyChanged('FederationIDS', oldVal, newVal),
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.isTeam = values.isTeam ?? false;
        this.__isteam = values.isTeam ?? false;
        Object.defineProperty(this, 'isTeam', {
            get(): boolean {
                return this.__isteam;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('isTeam', this.__isteam, value);
                this.__isteam = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"FederationIDS": [ ${this.FederationIDS.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"isTeam": ${JSON.stringify(this.isTeam)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of Club from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        FederationIDS?: number[];
        isTeam?: boolean;
        Name?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): Club {
        return new Club(values);
    }

    public static _FromServerJson(json: any): Club {
        return new Club({
            ID: json.ID,
            FederationIDS: json.FederationIDS,
            isTeam: json.isTeam,
            Name: json.Name ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of Club from another instance of Club.
     * @param other The Club you want to clone
     * @returns A cloned Club
     */
    public static FromOther(other: Club): Club {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return Club._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Club.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'FederationIDS' | 'isTeam' | 'Name')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'Club';
}
